"use client"

import { type WheelEvent, useMemo, useState } from "react"
import { useField, useFormikContext } from "formik"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import {
  useDebouncedValue,
  useOpaqueCredentialSessionId,
} from "@/components/deliveries/providers/nova-post/nova-post-form-hooks"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDeliveryIntegrationLocationsQuery } from "@/hooks/queries/delivery-integrations"
import type { DeliveryIntegrationConfig } from "@/lib/deliveries/config"
import {
  isNovaPostUkrainianSettlementQuery,
  normalizeNovaPostProviderConfigInput,
} from "@/lib/deliveries/providers/nova-post/nova-post-defaults"
import type {
  DeliveryLocationSearchResult,
  DeliveryLocationType,
} from "@/lib/deliveries/types"
import { cn } from "@/lib/utils"
import type { SearchDeliveryIntegrationLocationsInput } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


const SEARCH_DEBOUNCE_MS = 350
const SENDER_LOCATION_TYPES = [
  "branch",
  "locker",
  "pickup_point",
] as const

type SearchableLocation = DeliveryLocationSearchResult["locations"][number]

export function NovaPostSenderLocationSearchField({
  apiKey,
  providerConfig,
  integrationId,
}: {
  apiKey: string
  providerConfig: DeliveryIntegrationConfig["providerConfig"]
  integrationId?: string
}) {
  const tUi = useTranslations("Deliveries.DeliveriesProvidersNovaPostNovaPostSenderLocationSearchField")

  const [locationIdField, locationIdMeta] = useField<string>(
    "config.sender.location.providerLocationId",
  )
  const [locationCodeField] = useField<string | undefined>(
    "config.sender.location.providerLocationCode",
  )
  const [displayNameField] = useField<string | undefined>(
    "config.sender.location.displayName",
  )
  const [locationTypeField] = useField<Exclude<DeliveryLocationType, "address">>(
    "config.sender.location.type",
  )
  const { setFieldTouched, setFieldValue } = useFormikContext()
  const [cityQuery, setCityQuery] = useState("")
  const [branchQuery, setBranchQuery] = useState("")
  const [open, setOpen] = useState(false)
  const trimmedApiKey = apiKey.trim()
  const searchSessionId = useOpaqueCredentialSessionId(trimmedApiKey)
  const debouncedCityQuery = useDebouncedValue(
    cityQuery.trim(),
    SEARCH_DEBOUNCE_MS,
  )
  const debouncedBranchQuery = useDebouncedValue(
    branchQuery.trim(),
    SEARCH_DEBOUNCE_MS,
  )
  const hasCredentials = trimmedApiKey.length > 0 || Boolean(integrationId)
  const hasSearchableCity = isNovaPostUkrainianSettlementQuery(
    debouncedCityQuery,
  )
  const searchInput = useMemo(
    () => buildLocationSearchInput({
      apiKey: trimmedApiKey,
      branchQuery: debouncedBranchQuery,
      cityQuery: debouncedCityQuery,
      integrationId,
      providerConfig,
      searchSessionId,
    }),
    [
      debouncedBranchQuery,
      debouncedCityQuery,
      integrationId,
      providerConfig,
      searchSessionId,
      trimmedApiKey,
    ],
  )
  const locationsQuery = useDeliveryIntegrationLocationsQuery(
    searchInput,
    open && hasCredentials && hasSearchableCity && searchSessionId.length > 0,
  )
  const selectedLocation = toSelectedLocation({
    id: locationIdField.value,
    code: locationCodeField.value,
    displayName: displayNameField.value,
    type: locationTypeField.value,
  })
  const fieldError = locationIdMeta.touched && locationIdMeta.error
    ? locationIdMeta.error
    : null

  function handleSelect(location: SearchableLocation) {
    void setFieldValue("config.sender.location", location)
    void setFieldTouched(
      "config.sender.location.providerLocationId",
      true,
      false,
    )
    setOpen(false)
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div className="space-y-1.5">
        <Label htmlFor="sender-city-search"><span key={tUi("senderCity")}>{tUi("senderCity")}</span></Label>
        <Input
          id="sender-city-search"
          name="sender-city-search"
          value={cityQuery}
          onChange={event => setCityQuery(event.target.value)}
          placeholder="e.g. Київ…"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          <span key={tUi("enterTheCityWhereTheMerchantHandsOutgoing")}>{tUi("enterTheCityWhereTheMerchantHandsOutgoing")}</span></p>
      </div>

      <div className="space-y-1.5">
        <Label id="sender-location-label"><span key={tUi("senderLocation")}>{tUi("senderLocation")}</span></Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-labelledby="sender-location-label"
              aria-expanded={open}
              aria-required="true"
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError
                ? "sender-location-error"
                : "sender-location-description"}
              disabled={!hasCredentials
                || !isNovaPostUkrainianSettlementQuery(cityQuery)}
              className="h-auto min-h-10 w-full justify-between py-2 text-left font-normal"
            >
              <span key={selectedLocation
                  ? locationDisplayName(selectedLocation, tUi)
                  : tUi("selectASenderLocation")} className={cn(
                "min-w-0 truncate",
                !selectedLocation && "text-muted-foreground",
              )}>
                {selectedLocation
                  ? locationDisplayName(selectedLocation, tUi)
                  : tUi("selectASenderLocation")}
              </span>
              <ChevronsUpDown
                className="ml-2 h-4 w-4 shrink-0 opacity-50"
                aria-hidden="true"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] overscroll-contain p-0"
            onWheel={containLocationListWheel}
          >
            <Command shouldFilter={false}>
              <CommandInput
                value={branchQuery}
                onValueChange={setBranchQuery}
                placeholder={tUi("searchByBranchNumberOrAddress")}
                aria-label={tUi("searchSenderBranches")}
                name="sender-branch-search"
                autoComplete="off"
              />
              <CommandList aria-live="polite">
                {renderLocationSearchResults({
                  error: locationsQuery.error,
                  isFetching: locationsQuery.isFetching,
                  locations: locationsQuery.data?.locations ?? [],
                  selectedLocationId: locationIdField.value,
                  onSelect: handleSelect,
                }, tUi)}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p id="sender-location-description" className="text-xs text-muted-foreground">
          {senderLocationDescription({
            hasCredentials,
            hasSearchableCity: isNovaPostUkrainianSettlementQuery(cityQuery),
          }, tUi)}
        </p>
        {fieldError ? (
          <p
            id="sender-location-error"
            className="text-xs font-medium text-destructive"
          >
            {fieldError}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function containLocationListWheel(event: WheelEvent<HTMLDivElement>): void {
  // Popover content is portal-rendered, but React events still bubble through
  // the dialog tree. Keep wheel/touchpad scrolling inside the command list so
  // the dialog's scroll lock does not intercept it.
  event.stopPropagation()
}

function buildLocationSearchInput(input: {
  apiKey: string
  branchQuery: string
  cityQuery: string
  integrationId?: string
  providerConfig: DeliveryIntegrationConfig["providerConfig"]
  searchSessionId: string
}): SearchDeliveryIntegrationLocationsInput {
  const common = {
    provider: "nova_post" as const,
    query: input.branchQuery,
    settlementQuery: input.cityQuery,
    countryCode: "UA" as const,
    types: [...SENDER_LOCATION_TYPES],
    limit: 50,
  }

  if (input.apiKey.length > 0 || !input.integrationId) {
    return {
      ...common,
      credentialSource: "draft",
      apiKey: input.apiKey,
      searchSessionId: input.searchSessionId,
      providerConfig: normalizeNovaPostProviderConfigInput(input.providerConfig),
    }
  }

  return {
    ...common,
    credentialSource: "integration",
    integrationId: input.integrationId,
  }
}

function toSelectedLocation(input: {
  id: string
  code?: string
  displayName?: string
  type: Exclude<DeliveryLocationType, "address">
}): SearchableLocation | null {
  if (!input.id) return null
  return {
    type: input.type,
    countryCode: "UA",
    providerLocationId: input.id,
    providerLocationCode: input.code,
    displayName: input.displayName,
  }
}

function renderLocationSearchResults(input: {
  error: Error | null
  isFetching: boolean
  locations: SearchableLocation[]
  selectedLocationId: string
  onSelect: (location: SearchableLocation) => void
}, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderLocationSearchField">>) {
  const state = locationSearchState(input)
  switch (state.kind) {
    case "loading":
      return (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2
            className="h-4 w-4 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          <span><span key={tUi("searchingNovaPost")}>{tUi("searchingNovaPost")}</span></span>
        </div>
      )
    case "error":
      return (
        <p className="px-4 py-6 text-center text-sm text-destructive">
          {state.message}
        </p>
      )
    case "empty":
      return (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          <span key={tUi("noSendingLocationsFoundCheckTheCityOr")}>{tUi("noSendingLocationsFoundCheckTheCityOr")}</span></p>
      )
    case "ready":
      return (
        <CommandGroup>
          {state.locations.map(location => (
            <CommandItem
              key={location.providerLocationId}
              value={location.providerLocationId}
              onSelect={() => input.onSelect(location)}
              className="items-start py-2"
            >
              <Check
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0",
                  input.selectedLocationId === location.providerLocationId
                    ? "opacity-100"
                    : "opacity-0",
                )}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block break-words">
                  {locationDisplayName(location, tUi)}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {locationTypeLabel(location.type, tUi)}
                </span>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      )
    default: {
      const exhaustiveState: never = state
      return exhaustiveState
    }
  }
}

function locationSearchState(input: {
  error: Error | null
  isFetching: boolean
  locations: SearchableLocation[]
}):
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "empty" }
  | { kind: "ready"; locations: SearchableLocation[] } {
  if (input.isFetching) return { kind: "loading" }
  if (input.error) return { kind: "error", message: input.error.message }
  if (input.locations.length === 0) return { kind: "empty" }
  return { kind: "ready", locations: input.locations }
}

function senderLocationDescription(input: {
  hasCredentials: boolean
  hasSearchableCity: boolean
}, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderLocationSearchField">>): string {
  if (!input.hasCredentials) {
    return tUi("enterTheNovaPostAPIKeyBeforeSearching")
  }
  if (!input.hasSearchableCity) {
    return tUi("enterAtLeast2CityCharactersInUkrainian")
  }
  return tUi("searchByTheFamiliarBranchNumberOrAddress")
}

function locationDisplayName(location: SearchableLocation, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderLocationSearchField">>): string {
  if (location.displayName) return location.displayName
  if (location.providerLocationCode) {
    return tUi("locationWithCode", {type: locationTypeLabel(location.type, tUi), code: location.providerLocationCode})
  }
  return tUi("locationFallback", {type: locationTypeLabel(location.type, tUi)})
}

function locationTypeLabel(
  type: Exclude<DeliveryLocationType, "address">,
  tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderLocationSearchField">>,
): string {
  switch (type) {
    case "branch":
      return tUi("branch")
    case "locker":
      return tUi("parcelLocker")
    case "pickup_point":
      return tUi("pickupPoint")
    default: {
      const exhaustiveType: never = type
      return exhaustiveType
    }
  }
}
